import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Camera, Mic, Monitor, AlertTriangle, CheckCircle, XCircle, 
  Eye, Shield, Video, Volume2, Smartphone, Loader2, Upload
} from 'lucide-react';
import { toast } from 'sonner';

import { QRCodeSVG } from 'qrcode.react';

import {
  acquireSharedCamera, getSharedStream, startSharedFullRecording,
  stopSharedFullRecording, stopSharedStream, isSharedRecording
} from '../lib/proctorStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== SYSTEM CHECK COMPONENT ====================
export function SystemCheck({ settings, onComplete, onCancel, sessionId }) {
  const [checks, setChecks] = useState({
    camera: { status: 'pending', message: 'Checking camera...' },
    microphone: { status: 'pending', message: 'Checking microphone...' },
    audioLevel: { status: 'pending', message: 'Checking audio level...' },
    screenShare: { status: 'pending', message: 'Checking screen sharing...' },
    device: { status: 'pending', message: 'Checking device type...' },
    browser: { status: 'pending', message: 'Checking browser compatibility...' },
    fullscreen: { status: 'pending', message: 'Checking fullscreen support...' }
  });
  const [allPassed, setAllPassed] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [mobileQrUrl, setMobileQrUrl] = useState(null);
  const [mobileConnected, setMobileConnected] = useState(false);
  const [waitingForMobile, setWaitingForMobile] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mobilePollingRef = useRef(null);

  useEffect(() => {
    runSystemChecks();
    return () => {
      // For continuous-recording levels the camera stream is shared and must
      // keep recording through the rest of the session — don't stop it here.
      if (!settings.continuous_video_recording && streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const updateCheck = (key, status, message) => {
    setChecks(prev => ({
      ...prev,
      [key]: { status, message }
    }));
  };

  const runSystemChecks = async () => {
    setIsChecking(true);
    let passed = true;

    // Check device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (settings.device_restriction === 'desktop_only' && isMobile) {
      updateCheck('device', 'failed', 'This test requires a desktop/laptop computer');
      passed = false;
    } else {
      updateCheck('device', 'passed', isMobile ? 'Mobile device detected' : 'Desktop/Laptop detected');
    }

    // Check browser compatibility
    const hasMediaDevices = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (hasMediaDevices) {
      updateCheck('browser', 'passed', 'Browser supports required features');
    } else {
      updateCheck('browser', 'failed', 'Browser does not support camera/microphone access');
      passed = false;
    }

    // Check fullscreen support
    const fullscreenSupported = document.documentElement.requestFullscreen || 
                                document.documentElement.webkitRequestFullscreen ||
                                document.documentElement.mozRequestFullScreen;
    if (fullscreenSupported) {
      updateCheck('fullscreen', 'passed', 'Fullscreen mode supported');
    } else {
      updateCheck('fullscreen', 'failed', 'Fullscreen mode not supported');
      if (settings.fullscreen_required) passed = false;
    }

    // Check camera
    if (settings.camera_required) {
      try {
        let stream;
        if (settings.continuous_video_recording) {
          // Use the shared stream so recording can start immediately and persist.
          stream = await acquireSharedCamera({ audio: settings.audio_monitoring_enabled });
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        updateCheck('camera', 'passed', 'Camera access granted');

        // Start continuous recording the moment camera permission is granted.
        if (settings.continuous_video_recording && sessionId) {
          startSharedFullRecording(sessionId);
        }
      } catch (err) {
        updateCheck('camera', 'failed', `Camera access denied: ${err.message}`);
        passed = false;
      }
    } else {
      updateCheck('camera', 'skipped', 'Camera not required for this test');
    }

    // Check microphone
    if (settings.microphone_required) {
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        updateCheck('microphone', 'passed', 'Microphone access granted');

        // Audio level test (for maximum proctoring)
        if (settings.audio_check_required) {
          updateCheck('audioLevel', 'pending', 'Testing audio level... Please speak into your microphone');
          try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(audioStream);
            source.connect(analyser);
            analyser.fftSize = 256;
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            
            let maxLevel = 0;
            let checkCount = 0;
            const checkAudio = () => {
              analyser.getByteFrequencyData(dataArray);
              const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
              if (avg > maxLevel) maxLevel = avg;
              setAudioLevel(Math.min(100, Math.round(avg * 2)));
              checkCount++;
              if (checkCount < 30) { // Check for 3 seconds
                requestAnimationFrame(checkAudio);
              } else {
                audioCtx.close();
                if (maxLevel > 5) {
                  updateCheck('audioLevel', 'passed', `Audio detected (level: ${Math.round(maxLevel)})`);
                } else {
                  updateCheck('audioLevel', 'passed', 'Audio level low — please ensure mic works. You may proceed.');
                }
              }
            };
            checkAudio();
            // Wait for audio check to complete
            await new Promise(resolve => setTimeout(resolve, 3500));
          } catch (audioErr) {
            updateCheck('audioLevel', 'passed', 'Audio level check skipped');
          }
        } else {
          updateCheck('audioLevel', 'skipped', 'Audio level check not required');
        }

        audioStream.getTracks().forEach(track => track.stop());
      } catch (err) {
        updateCheck('microphone', 'failed', `Microphone access denied: ${err.message}`);
        updateCheck('audioLevel', 'skipped', 'Microphone unavailable');
        passed = false;
      }
    } else {
      updateCheck('microphone', 'skipped', 'Microphone not required for this test');
      updateCheck('audioLevel', 'skipped', 'Not required');
    }

    // Check screen sharing (for maximum proctoring)
    if (settings.screen_sharing_required) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        updateCheck('screenShare', 'passed', 'Screen sharing access granted');
        screenStream.getTracks().forEach(track => track.stop());
      } catch (err) {
        updateCheck('screenShare', 'failed', 'Screen sharing denied — required for this test');
        passed = false;
      }
    } else {
      updateCheck('screenShare', 'skipped', 'Screen sharing not required');
    }

    setAllPassed(passed);
    setIsChecking(false);

    // If all passed and mobile proctoring is enabled, generate QR code
    if (passed && settings.mobile_proctoring_enabled && sessionId) {
      generateMobileQr();
    }
  };

  const generateMobileQr = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!sessionId) {
        console.error('No session ID for mobile QR generation');
        return;
      }
      const formData = new FormData();
      formData.append('session_id', sessionId);
      const res = await fetch(`${API_URL}/api/proctoring/mobile/generate-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Mobile token generation failed:', err);
        return;
      }
      const data = await res.json();
      setMobileQrUrl(data.mobile_url);
      setWaitingForMobile(true);

      // Poll for mobile connection
      mobilePollingRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/proctoring/mobile/status/${sessionId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.connected) {
              clearInterval(mobilePollingRef.current);
              setMobileConnected(true);
              setWaitingForMobile(false);
            }
          }
        } catch {}
      }, 2000);
    } catch (err) {
      console.error('Failed to generate mobile QR:', err);
    }
  };

  useEffect(() => {
    return () => {
      if (mobilePollingRef.current) clearInterval(mobilePollingRef.current);
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'skipped': return <CheckCircle className="w-5 h-5 text-gray-400" />;
      default: return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
    }
  };

  return (
    <Card className="max-w-2xl mx-auto" data-testid="system-check">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-blue-600" />
          System Check
        </CardTitle>
        <CardDescription>
          Verifying your system meets the requirements for proctored testing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Camera Preview */}
        {settings.camera_required && (
          <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            {checks.camera.status === 'pending' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>
        )}

        {/* Check Results */}
        <div className="space-y-3">
          {Object.entries(checks).map(([key, check]) => (
            <div 
              key={key}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                check.status === 'failed' ? 'border-red-200 bg-red-50' :
                check.status === 'passed' ? 'border-green-200 bg-green-50' :
                'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                {key === 'camera' && <Camera className="w-5 h-5 text-gray-600" />}
                {key === 'microphone' && <Mic className="w-5 h-5 text-gray-600" />}
                {key === 'audioLevel' && <Volume2 className="w-5 h-5 text-gray-600" />}
                {key === 'screenShare' && <Monitor className="w-5 h-5 text-gray-600" />}
                {key === 'device' && <Smartphone className="w-5 h-5 text-gray-600" />}
                {key === 'browser' && <Monitor className="w-5 h-5 text-gray-600" />}
                {key === 'fullscreen' && <Monitor className="w-5 h-5 text-gray-600" />}
                <div>
                  <p className="font-medium capitalize">{key}</p>
                  <p className="text-sm text-gray-600">{check.message}</p>
                </div>
              </div>
              {getStatusIcon(check.status)}
            </div>
          ))}
        </div>

        {/* Mobile QR Pairing */}
        {allPassed && settings.mobile_proctoring_enabled && !mobileConnected && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center space-y-4" data-testid="mobile-qr-section">
            <h3 className="font-bold text-blue-900 text-lg">Mobile Camera Required</h3>
            <p className="text-sm text-blue-800">Scan this QR code with your phone to start the mobile camera</p>
            {mobileQrUrl ? (
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl shadow-md inline-block">
                  <QRCodeSVG value={mobileQrUrl} size={200} data-testid="mobile-qr-code" />
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-4">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            )}
            {mobileQrUrl && (
              <p className="text-xs text-gray-500 break-all">{mobileQrUrl}</p>
            )}
            {waitingForMobile && (
              <div className="flex items-center justify-center gap-2 text-blue-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Waiting for mobile to connect...</span>
              </div>
            )}
          </div>
        )}

        {allPassed && mobileConnected && settings.mobile_proctoring_enabled && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="mobile-connected-status">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">Mobile Camera Connected</p>
              <p className="text-sm text-green-700">360° monitoring is active</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          {isChecking ? (
            <Button disabled className="flex-1">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </Button>
          ) : allPassed && (!settings.mobile_proctoring_enabled || mobileConnected) ? (
            <Button onClick={() => onComplete(streamRef.current)} className="flex-1 bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Continue to Test
            </Button>
          ) : allPassed && settings.mobile_proctoring_enabled && !mobileConnected ? (
            <Button disabled className="flex-1">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Waiting for Mobile...
            </Button>
          ) : (
            <Button onClick={runSystemChecks} className="flex-1">
              Retry Checks
            </Button>
          )}
        </div>

        {!allPassed && !isChecking && (
          <Alert variant="destructive">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Please resolve the failed checks above before continuing with the test.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== IDENTITY VERIFICATION COMPONENT ====================
export function IdentityVerification({ sessionId, token, onComplete, onCancel }) {
  const [step, setStep] = useState('id'); // id, id_camera, live, verifying, result
  const [idImage, setIdImage] = useState(null);
  const [liveImage, setLiveImage] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const idVideoRef = useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      if (idVideoRef.current) {
        idVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error('Failed to access camera');
    }
  };

  // Assign the stream to idVideoRef whenever step changes to id_camera
  useEffect(() => {
    if (step === 'id_camera' && streamRef.current && idVideoRef.current) {
      idVideoRef.current.srcObject = streamRef.current;
    }
    if (step === 'live' && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [step]);

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setIdImage(event.target.result);
        setStep('live');
      };
      reader.readAsDataURL(file);
    }
  };

  const captureIdFromCamera = () => {
    if (!idVideoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = idVideoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const image = canvas.toDataURL('image/jpeg', 0.8);
    setIdImage(image);
    setStep('live');
  };

  const captureLivePhoto = () => {
    const image = captureImage();
    if (image) {
      setLiveImage(image);
      setStep('verifying');
      verifyIdentity(image);
    }
  };

  const verifyIdentity = async (liveImg) => {
    setIsVerifying(true);
    try {
      // Convert base64 to blob
      const idBlob = await fetch(idImage).then(r => r.blob());
      const liveBlob = await fetch(liveImg).then(r => r.blob());

      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('id_document', idBlob, 'id_document.jpg');
      formData.append('live_photo', liveBlob, 'live_photo.jpg');

      const response = await fetch(`${API_URL}/api/proctoring/verify-identity`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      setResult(data);
      setStep('result');

      // Always proceed — just capture photos, skip strict AI matching
      toast.success('ID and photo captured successfully!');
      setTimeout(() => onComplete(), 1500);
    } catch (err) {
      // Even on error, allow proceeding — photos are non-blocking
      toast.info('ID capture completed. Proceeding to test.');
      setTimeout(() => onComplete(), 1500);
    } finally {
      setIsVerifying(false);
    }
  };

  const retry = () => {
    setIdImage(null);
    setLiveImage(null);
    setResult(null);
    setStep('id');
    // Re-assign camera stream to video refs on next render
  };

  return (
    <Card className="max-w-4xl mx-auto" data-testid="identity-verification">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-blue-600" />
          Identity Verification
        </CardTitle>
        <CardDescription>
          Please verify your identity by providing your Aadhaar Card or Passport and taking a live photo
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Steps */}
        <div className="flex justify-between mb-6">
          {['Capture / Upload ID', 'Live Photo', 'Verification'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                ((step === 'id' || step === 'id_camera') && idx === 0) || (step === 'live' && idx === 1) || ((step === 'verifying' || step === 'result') && idx === 2)
                  ? 'bg-blue-600 text-white'
                  : idx < ['id', 'live', 'verifying'].indexOf(step === 'id_camera' ? 'id' : step)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
              }`}>
                {idx + 1}
              </div>
              <span className="ml-2 text-sm hidden sm:inline">{label}</span>
              {idx < 2 && <div className="w-12 h-0.5 bg-gray-200 mx-2" />}
            </div>
          ))}
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {step === 'id' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 font-medium text-center">Choose how to provide your ID document</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Option 1: Show to Camera */}
              <div
                onClick={() => setStep('id_camera')}
                className="border-2 border-gray-200 hover:border-blue-400 rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-blue-50"
                data-testid="id-capture-camera-option"
              >
                <Camera className="w-14 h-14 text-blue-500 mx-auto mb-4" />
                <p className="font-semibold text-gray-800 text-lg">Show to Camera</p>
                <p className="text-sm text-gray-500 mt-2">Hold your ID in front of the camera and capture</p>
              </div>
              {/* Option 2: Upload from Device */}
              <div className="border-2 border-gray-200 hover:border-blue-400 rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-blue-50 relative"
                data-testid="id-upload-file-option"
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleIdUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  id="id-upload"
                />
                <Upload className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <p className="font-semibold text-gray-800 text-lg">Upload from Device</p>
                <p className="text-sm text-gray-500 mt-2">Select an image file from your computer or phone</p>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                Make sure the ID is clearly visible and all text is readable.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'id_camera' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 font-medium text-center">Hold your Aadhaar Card or Passport clearly in front of the camera</p>
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden max-w-2xl mx-auto">
              <video
                ref={idVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border-4 border-dashed border-white/40 m-6 rounded-lg pointer-events-none" />
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <span className="bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                  Position your ID within the frame
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('id')} className="flex-1" data-testid="id-camera-back-btn">
                Back
              </Button>
              <Button onClick={captureIdFromCamera} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="id-camera-capture-btn">
                <Camera className="w-4 h-4 mr-2" />
                Capture ID
              </Button>
            </div>
            <Alert>
              <AlertDescription>
                Ensure the ID is well-lit, not blurry, and all text is readable.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'live' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium mb-2">Your ID Document</p>
                <img src={idImage} alt="ID Document" className="w-full rounded-lg border" />
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Live Camera</p>
                <div className="relative aspect-[4/3] bg-gray-900 rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 border-4 border-dashed border-white/50 m-4 rounded-lg" />
                </div>
              </div>
            </div>
            <Button onClick={captureLivePhoto} className="w-full">
              <Camera className="w-4 h-4 mr-2" />
              Capture Photo
            </Button>
          </div>
        )}

        {step === 'verifying' && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium">Verifying your identity...</p>
            <p className="text-gray-600">This may take a few seconds</p>
          </div>
        )}

        {step === 'result' && result && (
          <div className="text-center py-8">
            {result.verified ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-xl font-medium text-green-700">Identity Verified!</p>
                <p className="text-gray-600">Match confidence: {result.match_score}%</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-xl font-medium text-red-700">Verification Failed</p>
                <p className="text-gray-600">{result.message}</p>
                <Button onClick={retry} className="mt-4">
                  Try Again
                </Button>
              </>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            Cancel Test
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== PROCTORING OVERLAY COMPONENT ====================
export function ProctoringOverlay({ 
  sessionId, 
  settings, 
  token,
  onTerminate,
  onViolationUpdate,
  children 
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [trustScore, setTrustScore] = useState(100);
  const [violations, setViolations] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [gazeScore, setGazeScore] = useState(100);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const audioIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Video recording refs for violation clips
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const isCapturingClipRef = useRef(false);
  
  // Continuous full recording refs
  const fullRecorderRef = useRef(null);
  const fullChunksRef = useRef([]);
  const fullChunkIndexRef = useRef(0);
  const fullUploadIntervalRef = useRef(null);

  useEffect(() => {
    initializeProctoring();
    return () => cleanup();
  }, []);

  const initializeProctoring = async () => {
    // Start camera if needed
    if (settings.camera_required || settings.face_detection_enabled) {
      try {
        let stream;
        if (settings.continuous_video_recording) {
          // Reuse the shared stream already recording since the camera grant.
          stream = getSharedStream();
          if (!stream) {
            stream = await acquireSharedCamera({ audio: settings.audio_monitoring_enabled });
          }
          // Ensure continuous recording is running (covers any earlier failure).
          if (!isSharedRecording()) {
            startSharedFullRecording(sessionId);
          }
        } else {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: settings.audio_monitoring_enabled
          });
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        // Start continuous video recording for violation clips
        startVideoRecording(stream);

        // Audio monitoring (analysis) if enabled
        if (settings.audio_monitoring_enabled) {
          startAudioMonitoring(stream);
        }
      } catch (err) {
        console.error('Failed to start camera:', err);
      }
    }

    // Fullscreen is now handled by TakeTest.jsx via user gesture click
    // Just set up the fullscreen change listener to detect exits
    if (settings.fullscreen_required) {
      const isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      setIsFullscreen(isFs);
    }

    // Set up visibility change listener (tab switches)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Set up fullscreen change listener
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    // Start random photo capture
    if (settings.random_photos_enabled) {
      scheduleNextCapture();
    }
  };

  const startAudioMonitoring = (stream) => {
    // Record audio in intervals and analyze for voices
    const recordAndAnalyze = () => {
      if (!stream || !stream.active) return;
      
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) return;
      
      // Create a new stream with only audio
      const audioStream = new MediaStream(audioTracks);
      
      try {
        const mediaRecorder = new MediaRecorder(audioStream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };
        
        mediaRecorder.onstop = async () => {
          if (audioChunksRef.current.length === 0) return;
          
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          
          // Only analyze if audio is substantial (more than 1KB)
          if (audioBlob.size > 1024) {
            await analyzeAudioChunk(audioBlob);
          }
          
          audioChunksRef.current = [];
        };
        
        // Record for 10 seconds
        mediaRecorder.start();
        setIsRecordingAudio(true);
        
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            setIsRecordingAudio(false);
          }
        }, 10000);
        
      } catch (err) {
        console.error('Failed to start audio recording:', err);
      }
    };
    
    // Start first recording after 5 seconds, then every 30 seconds
    setTimeout(() => {
      recordAndAnalyze();
      audioIntervalRef.current = setInterval(recordAndAnalyze, 30000);
    }, 5000);
  };

  // Video recording for violation clips
  const startVideoRecording = (stream) => {
    try {
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';
      
      const recorder = new MediaRecorder(stream, { 
        mimeType, 
        videoBitsPerSecond: 500000 // 500kbps for small clips
      });
      videoRecorderRef.current = recorder;
      videoChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
          // Keep only last ~6 seconds of chunks (timeslice=1000ms, so ~6 chunks)
          if (videoChunksRef.current.length > 6) {
            videoChunksRef.current.shift();
          }
        }
      };

      // Record in 1-second slices for rolling buffer
      recorder.start(1000);
      console.log('Video recording started for violation clips');
    } catch (err) {
      console.error('Failed to start video recording:', err);
    }
  };

  // Continuous full recording — uploads chunks every 60s
  const startFullRecording = (stream) => {
    try {
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8' : 'video/webm';
      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 300000 });
      fullRecorderRef.current = recorder;
      fullChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          fullChunksRef.current.push(event.data);
        }
      };

      recorder.start(5000); // 5s timeslice
      console.log('Full continuous recording started');

      // Upload accumulated chunks every 60 seconds
      fullUploadIntervalRef.current = setInterval(() => {
        uploadFullRecordingChunk(false);
      }, 60000);
    } catch (err) {
      console.error('Failed to start full recording:', err);
    }
  };

  const uploadFullRecordingChunk = async (isFinal) => {
    if (fullChunksRef.current.length === 0) return;
    const chunks = [...fullChunksRef.current];
    fullChunksRef.current = [];
    const mimeType = fullRecorderRef.current?.mimeType || 'video/webm';
    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size < 5000) return; // Skip tiny chunks

    try {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const b64 = reader.result.split(',')[1];
        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('video_base64', b64);
        formData.append('chunk_index', fullChunkIndexRef.current);
        formData.append('is_final', isFinal);
        formData.append('mime_type', mimeType);
        fullChunkIndexRef.current += 1;
        await fetch(`${API_URL}/api/proctoring/upload-full-recording`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        console.log(`Full recording chunk ${fullChunkIndexRef.current - 1} uploaded`);
      };
    } catch (err) {
      console.error('Failed to upload recording chunk:', err);
    }
  };

  const captureViolationClip = async (violationType) => {
    // Prevent multiple simultaneous clip captures
    if (isCapturingClipRef.current) return;
    if (!videoRecorderRef.current || videoRecorderRef.current.state !== 'recording') return;
    if (videoChunksRef.current.length === 0) return;

    isCapturingClipRef.current = true;
    try {
      // Grab current buffered chunks (last ~5 seconds)
      const clipChunks = [...videoChunksRef.current];
      const mimeType = videoRecorderRef.current.mimeType || 'video/webm';
      const videoBlob = new Blob(clipChunks, { type: mimeType });
      
      // Only upload if clip is substantial (>10KB)
      if (videoBlob.size < 10240) {
        isCapturingClipRef.current = false;
        return;
      }

      // Convert to base64 for upload
      const reader = new FileReader();
      reader.readAsDataURL(videoBlob);
      reader.onloadend = async () => {
        try {
          const base64Video = reader.result.split(',')[1];
          const formData = new FormData();
          formData.append('session_id', sessionId);
          formData.append('violation_type', violationType);
          formData.append('video_base64', base64Video);
          formData.append('mime_type', mimeType);

          await fetch(`${API_URL}/api/proctoring/upload-violation-clip`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          console.log(`Violation clip uploaded for: ${violationType}`);
        } catch (err) {
          console.error('Failed to upload violation clip:', err);
        } finally {
          isCapturingClipRef.current = false;
        }
      };
    } catch (err) {
      console.error('Failed to capture violation clip:', err);
      isCapturingClipRef.current = false;
    }
  };

  const analyzeAudioChunk = async (audioBlob) => {
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('audio_base64', base64Audio);
        formData.append('audio_format', 'webm');
        
        try {
          const response = await fetch(`${API_URL}/api/proctoring/analyze-audio`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          
          const data = await response.json();
          
          if (data.trust_score !== undefined) {
            setTrustScore(data.trust_score);
          }
          
          if (data.voices_detected) {
            setViolations(prev => [...prev, 'voice_detected']);
            captureViolationClip('voice_detected');
            
            let warningMsg = 'Voice activity detected. Please remain silent during the exam.';
            if (data.is_suspicious) {
              warningMsg = 'Suspicious audio detected! Discussion about exam content is not allowed.';
            }
            if (data.voice_count > 1) {
              warningMsg = 'Multiple voices detected! Please ensure you are taking the exam alone.';
            }
            
            showWarningMessage(warningMsg);
          }
        } catch (err) {
          console.error('Failed to analyze audio:', err);
        }
      };
    } catch (err) {
      console.error('Failed to process audio chunk:', err);
    }
  };

  const cleanup = () => {
    if (settings.continuous_video_recording) {
      // Stop the shared continuous recording, upload final chunk, then release.
      stopSharedFullRecording().finally(() => stopSharedStream());
    } else {
      // Upload final recording chunk (non-shared fallback path)
      if (fullRecorderRef.current && fullRecorderRef.current.state === 'recording') {
        fullRecorderRef.current.stop();
        uploadFullRecordingChunk(true);
      }
      if (fullUploadIntervalRef.current) {
        clearInterval(fullUploadIntervalRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
    if (captureIntervalRef.current) {
      clearTimeout(captureIntervalRef.current);
    }
    if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (videoRecorderRef.current && videoRecorderRef.current.state === 'recording') {
      videoRecorderRef.current.stop();
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
  };

  const enterFullscreen = async () => {
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      reportViolation('tab_switch');
      setTabSwitches(prev => prev + 1);
      captureViolationClip('tab_switch');
    }
  };

  const handleFullscreenChange = () => {
    const isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
    setIsFullscreen(isFs);
    
    if (!isFs && settings.fullscreen_required) {
      reportViolation('fullscreen_exit');
      showWarningMessage('Please return to fullscreen mode to continue the test');
      captureViolationClip('fullscreen_exit');
      
      // Auto re-enter fullscreen
      setTimeout(() => enterFullscreen(), 1000);
    }
  };

  const showWarningMessage = (message) => {
    setWarningMessage(message);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 5000);
  };

  const scheduleNextCapture = () => {
    // Capture every 15 seconds for continuous monitoring
    const interval = 15000;
    
    captureIntervalRef.current = setTimeout(() => {
      captureAndAnalyze();
      scheduleNextCapture();
    }, interval);
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const imageBase64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];

    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('image_base64', imageBase64);

      const response = await fetch(`${API_URL}/api/proctoring/capture-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (data.trust_score !== undefined) {
        setTrustScore(data.trust_score);
      }
      if (data.gaze_score !== undefined) {
        setGazeScore(data.gaze_score);
      }
      
      if (data.violations && data.violations.length > 0) {
        setViolations(prev => {
          const updated = [...prev, ...data.violations];
          onViolationUpdate?.(updated.length, data.trust_score);
          return updated;
        });
        
        // Show warnings for detected violations
        data.violations.forEach(v => {
          if (v === 'no_face') {
            showWarningMessage('No face detected. Please ensure your face is visible.');
          } else if (v === 'multiple_faces') {
            showWarningMessage('Multiple faces detected. Please ensure you are alone.');
          } else if (v === 'phone_detected') {
            showWarningMessage('Phone detected. Electronic devices are not allowed.');
          } else if (v === 'looking_away') {
            showWarningMessage('Please keep your eyes on the screen.');
          }
          // Capture video clip for each violation type
          captureViolationClip(v);
        });
      }
    } catch (err) {
      console.error('Failed to capture and analyze:', err);
    }
  };

  const reportViolation = async (violationType, evidenceBase64 = null) => {
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('violation_type', violationType);
      if (evidenceBase64) {
        formData.append('evidence_base64', evidenceBase64);
      }

      const response = await fetch(`${API_URL}/api/proctoring/report-violation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (data.trust_score !== undefined) {
        setTrustScore(data.trust_score);
      }

      // Update parent with new violation count
      setViolations(prev => {
        const updated = [...prev, violationType];
        onViolationUpdate?.(updated.length, data.trust_score);
        return updated;
      });
      
      if (data.should_terminate) {
        toast.error(data.terminate_reason || 'Test terminated due to policy violations');
        onTerminate(data.terminate_reason);
      }
    } catch (err) {
      console.error('Failed to report violation:', err);
    }
  };

  const getTrustColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="relative min-h-screen">
      {/* Proctoring Status Bar */}
      <div className="fixed top-0 left-0 right-0 bg-gray-900 text-white py-2 px-4 z-50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-sm">Proctored Test</span>
          </div>
          
          {settings.camera_required && (
            <div className="flex items-center gap-1">
              <Camera className={`w-4 h-4 ${streamRef.current ? 'text-green-400' : 'text-red-400'}`} />
            </div>
          )}
          
          {settings.audio_monitoring_enabled && (
            <div className="flex items-center gap-1">
              <Mic className="w-4 h-4 text-green-400" />
            </div>
          )}
          
          {settings.fullscreen_required && (
            <div className="flex items-center gap-1">
              <Monitor className={`w-4 h-4 ${isFullscreen ? 'text-green-400' : 'text-red-400'}`} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm">Trust Score:</span>
            <span className={`font-bold ${getTrustColor(trustScore)}`}>
              {trustScore.toFixed(0)}%
            </span>
          </div>
          
          {settings.gaze_score_tracking && (
            <div className="flex items-center gap-2" data-testid="gaze-score-display">
              <span className="text-sm">Gaze:</span>
              <span className={`font-bold ${getTrustColor(gazeScore)}`}>
                {gazeScore.toFixed(0)}%
              </span>
            </div>
          )}

          {settings.continuous_video_recording && (
            <div className="flex items-center gap-1.5 text-red-400 animate-pulse" data-testid="recording-badge">
              <span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span>
              <span className="text-xs font-semibold">REC</span>
            </div>
          )}
          
          {tabSwitches > 0 && (
            <Badge variant="destructive" className="text-xs">
              Tab Switches: {tabSwitches}/{settings.max_tab_switches}
            </Badge>
          )}
        </div>
      </div>

      {/* Camera Preview (small) */}
      {settings.camera_required && (
        <div className="fixed bottom-4 right-4 w-32 h-24 bg-gray-900 rounded-lg overflow-hidden shadow-lg z-40 border-2 border-gray-700">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Warning Overlay */}
      {showWarning && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
          <Alert variant="destructive" className="shadow-lg">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{warningMessage}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main Content */}
      <div className="pt-12">
        {children}
      </div>
    </div>
  );
}

// ==================== PROCTORING SETTINGS SELECTOR (Admin) ====================
export function ProctoringSettingsSelector({ value, onChange }) {
  const levels = [
    { 
      id: 'none', 
      name: 'No Proctoring', 
      description: 'Standard test without monitoring',
      icon: null,
      color: 'gray'
    },
    { 
      id: 'basic', 
      name: 'Basic', 
      description: 'Fullscreen mode + tab switch monitoring',
      icon: <Monitor className="w-5 h-5" />,
      color: 'blue'
    },
    { 
      id: 'standard', 
      name: 'Standard', 
      description: 'Basic + random photos + face detection',
      icon: <Camera className="w-5 h-5" />,
      color: 'green'
    },
    { 
      id: 'strict', 
      name: 'Strict', 
      description: 'Standard + audio monitoring + eye tracking',
      icon: <Eye className="w-5 h-5" />,
      color: 'yellow'
    },
    { 
      id: 'maximum', 
      name: 'Maximum', 
      description: 'Full video recording + gaze analysis + screen sharing + audio check + identity verification',
      icon: <Shield className="w-5 h-5" />,
      color: 'red'
    },
    { 
      id: 'maximum_mobile', 
      name: 'Maximum + Mobile', 
      description: 'All maximum features + mobile QR 360° proctoring with dual camera',
      icon: <Smartphone className="w-5 h-5" />,
      color: 'red'
    }
  ];

  return (
    <div className="space-y-3" data-testid="proctoring-settings-selector">
      <label className="text-sm font-medium text-gray-700">Proctoring Level</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {levels.map(level => (
          <div
            key={level.id}
            onClick={() => onChange(level.id)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              value === level.id 
                ? `border-${level.color}-500 bg-${level.color}-50` 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`proctoring-level-${level.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              {level.icon}
              <span className="font-medium">{level.name}</span>
              {value === level.id && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
            </div>
            <p className="text-sm text-gray-600">{level.description}</p>
          </div>
        ))}
      </div>
      
      {value && value !== 'none' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Features Enabled:</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {value !== 'none' && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Fullscreen Mode</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Tab Switch Detection</span>
                </div>
              </>
            )}
            {['standard', 'strict', 'maximum'].includes(value) && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Random Photo Capture</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Face Detection</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Desktop Only</span>
                </div>
              </>
            )}
            {['strict', 'maximum', 'maximum_mobile'].includes(value) && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Audio Monitoring</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Eye Tracking</span>
                </div>
              </>
            )}
            {value === 'maximum' && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Identity Verification</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Continuous Video Recording</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Gaze Analysis Score</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Screen Sharing Check</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Audio Level Check</span>
                </div>
              </>
            )}
            {value === 'maximum_mobile' && (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>All Maximum Features</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Mobile QR 360° Camera</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>AI Mobile Placement Validation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Dual Video Recording</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Combined Trust Score</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default { SystemCheck, IdentityVerification, ProctoringOverlay, ProctoringSettingsSelector };
